import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '@/hooks/useApi';
import { PageContainer } from '@/components/common/PageContainer';
import { DataTable, type Column } from '@/components/common/DataTable';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, ClipboardList, Loader2, Filter, ChevronRight, Eye, CheckCircle } from 'lucide-react';
import { Manifest, MANIFEST_TYPE_LABELS, MANIFEST_STATUS_LABELS } from '@/types/manifest.types';
import { ROUTES } from '@/lib/constants';
import { formatDate } from '@/lib/formatters';
import { ManifestFilterSidebar, ManifestFilterValues } from '@/components/features/manifests/ManifestFilterSidebar';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';

const initialFilters: ManifestFilterValues = {
  date: '',
  status: '',
};

export default function ManifestsList() {
  const api = useApi();
  const navigate = useNavigate();
  const [manifests, setManifests] = useState<Manifest[]>([]);
  const [hasFetched, setHasFetched] = useState(false);
  const [filters, setFilters] = useState<ManifestFilterValues>(initialFilters);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [selectedRows, setSelectedRows] = useState<(string | number)[]>([]);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [generateDate, setGenerateDate] = useState(new Date().toISOString().split('T')[0]);
  const [generating, setGenerating] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [createdManifests, setCreatedManifests] = useState<{ id: string; manifest_number: string; manifest_type: string }[]>([]);

  const fetchManifests = useCallback(async () => {
    try {
      const params: Record<string, string> = {};
      if (filters.date) params.date = filters.date;
      if (filters.status) params.status = filters.status;

      const list: any = await api.get('/api/v1/dashboard/manifests', params);
      setManifests(Array.isArray(list) ? list : []);
    } catch (error) {
      toast.error('فشل في جلب البيانات');
    } finally {
      setHasFetched(true);
    }
  }, [filters.date, filters.status]);

  useEffect(() => {
    fetchManifests();
  }, [fetchManifests]);

  const handleGenerate = async () => {
    if (!generateDate) {
      toast.error('يرجى اختيار التاريخ');
      return;
    }
    setGenerating(true);
    try {
      const res: any = await api.post('/api/v1/dashboard/manifests/generate', {
        date: generateDate,
      });
      const created: any[] = res?.data ?? res ?? [];
      setShowGenerateDialog(false);
      setCreatedManifests(created);
      setShowSuccessDialog(true);
      fetchManifests();
    } catch (error: any) {
      const message = error?.response?.data?.message || 'فشل في توليد القوائم';
      toast.error(message);
    } finally {
      setGenerating(false);
    }
  };

  const handleFilterChange = (key: keyof ManifestFilterValues, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value === 'all' ? '' : value }));
  };

  const handleClearFilters = () => {
    setFilters(initialFilters);
  };

  const getStatusVariant = (status: string): 'default' | 'secondary' | 'destructive' | 'outline' => {
    switch (status) {
      case 'completed': return 'default';
      case 'in_progress': return 'outline';
      case 'draft': return 'secondary';
      default: return 'secondary';
    }
  };

  const columns: Column<Manifest>[] = [
    {
      header: 'القائمة',
      accessor: (row) => (
        <div>
          <div className="font-medium text-sm">
            {MANIFEST_TYPE_LABELS[row.manifest_type] || row.manifest_type}
          </div>
          <div className="font-mono text-xs text-muted-foreground">({row.manifest_number})</div>
        </div>
      ),
    },
    {
      header: 'السائق',
      accessor: (row) => (
        <span className="text-sm">{(row as any).driver?.user?.name || 'غير معين'}</span>
      ),
    },
    {
      header: 'الحالة',
      accessor: (row) => (
        <Badge variant={getStatusVariant(row.status)}>
          {MANIFEST_STATUS_LABELS[row.status] || row.status}
        </Badge>
      ),
    },
    {
      header: 'الرحلات',
      accessor: (row) => (
        <span className="text-sm text-center">
          {row.completed_trips}/{row.total_trips}
        </span>
      ),
    },
    {
      header: 'التاريخ',
      accessor: (row) => (
        <span className="text-sm text-muted-foreground">{formatDate(row.date)}</span>
      ),
    },
  ];

  const renderActions = (row: Manifest) => (
    <Button
      variant="ghost"
      size="icon"
      className="h-8 w-8"
      onClick={() => navigate(ROUTES.MANIFEST_DETAILS(row.id))}
      title="عرض التفاصيل"
    >
      <Eye className="h-4 w-4" />
    </Button>
  );

  const FilterSidebarContent = (
    <ManifestFilterSidebar
      filters={filters}
      onFilterChange={handleFilterChange}
      onClearFilters={handleClearFilters}
    />
  );

  return (
    <PageContainer>
      {/* Header */}
      <div className="mb-4 md:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ChevronRight className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl md:text-2xl font-bold">القوائم</h1>
              <p className="text-sm text-muted-foreground">إدارة قوائم التوصيل والاستلام</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Sheet open={showMobileFilters} onOpenChange={setShowMobileFilters}>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon" className="lg:hidden">
                  <Filter className="h-4 w-4" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 p-4">
                <div className="mt-6">{FilterSidebarContent}</div>
              </SheetContent>
            </Sheet>
            <Button onClick={() => setShowGenerateDialog(true)} className="gap-2">
              <Plus className="h-4 w-4" />
              توليد القوائم
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content with Sidebar */}
      <div className="flex gap-4 ">
        <div className="hidden lg:block">
          {FilterSidebarContent}
        </div>
        <div className="flex-1 min-w-0">
          {!hasFetched ? (
            <LoadingSkeleton type="table" rows={8} />
          ) : manifests.length === 0 ? (
            <EmptyState
              icon={<ClipboardList className="h-16 w-16" />}
              title="لا توجد قوائم"
              description="قم بتوليد القوائم للبدء في عمليات التوصيل"
              action={{ label: 'توليد القوائم', onClick: () => setShowGenerateDialog(true) }}
            />
          ) : (
            <DataTable
              data={manifests}
              columns={columns}
              actions={renderActions}
              selectable={true}
              selectedRows={selectedRows}
              onSelectionChange={setSelectedRows}
            />
          )}
        </div>
      </div>

      {/* Success Dialog */}
      <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <DialogContent className="sm:max-w-[480px]" dir="rtl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              تم توليد البيانات بنجاح
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            {createdManifests.length === 0 ? (
              <p className="text-sm text-muted-foreground">لا توجد بيانات جديدة.</p>
            ) : (
              <div className="divide-y divide-border rounded-md border">
                {createdManifests.map((manifest) => (
                  <div key={manifest.id} className="flex items-center justify-between px-4 py-3">
                    <div>
                      <div className="text-sm font-medium">
                        {MANIFEST_TYPE_LABELS[manifest.manifest_type] || manifest.manifest_type}
                      </div>
                      <div className="font-mono text-xs text-muted-foreground">
                        {manifest.manifest_number}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setShowSuccessDialog(false);
                        navigate(ROUTES.MANIFEST_DETAILS(manifest.id));
                      }}
                    >
                      عرض
                    </Button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-end pt-2">
              <Button variant="outline" onClick={() => setShowSuccessDialog(false)}>
                إغلاق
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Generate Dialog */}
      <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
        <DialogContent className="sm:max-w-[450px]" dir="rtl">
          <DialogHeader>
            <DialogTitle>توليد القوائم</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <label className="text-sm font-medium">التاريخ</label>
              <Input
                type="date"
                value={generateDate}
                onChange={(e) => setGenerateDate(e.target.value)}
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button onClick={handleGenerate} disabled={generating} className="flex-1">
                {generating && <Loader2 className="ms-2 h-4 w-4 animate-spin" />}
                توليد القوائم
              </Button>
              <Button variant="outline" onClick={() => setShowGenerateDialog(false)}>
                إلغاء
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
